/* eslint-disable max-lines */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  Connect,
  PropsFromRedux,
} from './SearchQuerySectionDecisionsContainer';

import { FiltersToggle } from '../../../../../../../components/filters-toggle/FiltersToggle';
import { SearchBarSection } from '../../search-bar-section/SearchBarSection';
import { RawSearchResultDecisions } from '../../../../../../../search/components/search-results/decision/SearchResultDecisions';
import { Loader } from '../../../../../../../components/loader/Loader';
import { ResetQueryMessage } from '../ResetQueryMessage';
import { SeeMoreMessage } from '../SeeMoreMessage';

import { useUser } from '../../../../../../../user/useUser';
import {
  buildArticleFilters,
  getCASSAggregation,
  getCEAggregation,
  getFilters,
} from '../utils/utils';
import { useRestrictedModal } from '../../../../../../../components/modal/useRestrictedModal';
import { doctrineAnalytics } from '../../../../../../../utils/analyticsUtils';
import { searchWithinRestrictedArticles } from '../../../../../legislation.api';
import { LegislationContextType } from '../../../../../legislation.types';

import styles from './search_query_section_decisions.module.scss';

type Props = {
  decisions: LegislationContextType['decisions'];
  articleIds: string[];
  articleTitle: string;
  referencesDecisionsIds: string[];
  QPCDecisionsIds: string[];
  articleId: string;
  textNature: string;
  initialVersionId: string;
} & PropsFromRedux;

export const RawSearchQuerySectionDecisions: FC<Props> = ({
  query,
  articleTitle,
  initialVersionId,
  articleId,
  articleIds,
  textNature,
  decisions,
  QPCDecisionsIds,
  referencesDecisionsIds,
  updateDecisionsFolders,
  updateFolders,
}) => {
  const intl = useIntl();
  const { openRestrictedModal } = useRestrictedModal();
  const { pathname, search } = useLocation();
  const { loggedIn } = useUser();
  const shouldPersistQuery = !!(loggedIn && query);
  const searchParams = new URLSearchParams(search);
  const filterParam = searchParams.get('filter');
  const getDocumentIds = (value: string) =>
    (value === 'reference' && referencesDecisionsIds) ||
    (value === 'qpc' && QPCDecisionsIds) ||
    undefined;

  const [currentQuery, setCurrentQuery] = useState<string>(query);
  const [resultHits, setResultHits] = useState(
    shouldPersistQuery ? [] : decisions.data?.hits,
  );
  const [filterValue, setFilterValue] = useState<string>('sort_relevance');
  const [referenceDecisions, setReferenceDecisions] = useState<{}[]>(
    referencesDecisionsIds,
  );
  const [QPCDecisions, setQPCDecisions] = useState<{}[]>(QPCDecisionsIds);
  const [aggregationsFilters, setAggregationsFilters] = useState({
    cass: decisions.filters?.cass || { count: 0 },
    ce: decisions.filters?.ce || { count: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [resultTotalHits, setResultTotalHits] = useState(
    shouldPersistQuery ? 0 : decisions.data?.totalHits,
  );
  const [resultTheEnd, setResultTheEnd] = useState(
    shouldPersistQuery ? false : decisions.data?.theEnd,
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await searchWithinRestrictedArticles({
        q: currentQuery,
        type: 'arret',
        restrict_to_law_article_ids: articleIds,
        size: 6,
        juridiction: getFilters(filterValue),
      });
      setAggregationsFilters({
        ...aggregationsFilters,
        cass: getCASSAggregation(result.aggregations),
        ce: getCEAggregation(result.aggregations),
      });
      setResultHits(result.hits);
      setResultTotalHits(result.totalHits);
      setResultTheEnd(result.theEnd);
      setLoading(false);
    };

    const fetchReferenceDecisions = async () => {
      const result = await searchWithinRestrictedArticles({
        q: currentQuery,
        type: 'arret',
        restrict_to_law_article_ids: articleIds,
        restrict_to_document_ids: referencesDecisionsIds,
        size: referencesDecisionsIds.length,
      });
      setReferenceDecisions(result.hits);
    };

    const fetchQPCDecisions = async () => {
      const result = await searchWithinRestrictedArticles({
        q: currentQuery,
        type: 'arret',
        restrict_to_law_article_ids: articleIds,
        restrict_to_document_ids: QPCDecisionsIds,
        size: QPCDecisionsIds.length,
      });
      setQPCDecisions(result.hits);
    };

    if (shouldPersistQuery && query.length) {
      fetchData();
      if (referencesDecisionsIds.length) fetchReferenceDecisions();
      if (QPCDecisionsIds.length) fetchQPCDecisions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memoizedFilters = useMemo(
    () =>
      buildArticleFilters({
        type: 'arret',
        loggedIn,
        QPCDecisions,
        referenceDecisions,
        aggregations: aggregationsFilters,
        intl,
      }),
    [QPCDecisions, intl, loggedIn, aggregationsFilters, referenceDecisions],
  );

  const onFilterClick = async (value: string) => {
    if (!loggedIn) {
      openRestrictedModal({ source: `arret_${value}` });
      return;
    }
    doctrineAnalytics('Legislation DecisionCategoryClicked', {
      category_name: value,
      article_version_id: articleId,
      article_id: initialVersionId,
      legislationType: textNature,
    });
    setFilterValue(value);
    const documentIds = getDocumentIds(value);
    setLoading(true);
    const result = await searchWithinRestrictedArticles({
      q: currentQuery,
      type: 'arret',
      restrict_to_law_article_ids: articleIds,
      restrict_to_document_ids: documentIds,
      size: documentIds?.length || 6,
      chrono: value === 'sort_chrono',
      juridiction: getFilters(value),
    });
    setResultHits(result.hits);
    setResultTotalHits(result.totalHits);
    setResultTheEnd(result.theEnd);
    setLoading(false);
    if (value === 'reference') setReferenceDecisions(result.hits);
    if (value === 'qpc') setQPCDecisions(result.hits);
  };

  const onClearClicked = async () => {
    setLoading(true);
    const result = await searchWithinRestrictedArticles(
      {
        type: 'arret',
        restrict_to_law_article_ids: articleIds,
        restrict_to_document_ids: getDocumentIds(filterValue),
        size: getDocumentIds(filterValue)?.length || 6,
        chrono: filterValue === 'sort_chrono',
        juridiction: getFilters(filterValue),
      },
      loggedIn,
    );
    setAggregationsFilters({
      cass: decisions.filters?.cass || { count: 0 },
      ce: decisions.filters?.ce || { count: 0 },
    });
    setResultHits(result.hits);
    setResultTotalHits(result.totalHits);
    setResultTheEnd(result.theEnd);
    setCurrentQuery('');
    if (filterValue === 'reference') {
      setReferenceDecisions(result.hits);
      setQPCDecisions(QPCDecisionsIds);
    } else if (filterValue === 'qpc') {
      setQPCDecisions(result.hits);
      setReferenceDecisions(referencesDecisionsIds);
    } else {
      setReferenceDecisions(referencesDecisionsIds);
      setQPCDecisions(QPCDecisionsIds);
    }
    setLoading(false);
  };

  const source =
    (filterValue === 'reference' && 'legislation_reference') ||
    (filterValue === 'qpc' && 'legislation_qpc') ||
    undefined;

  const renderFilterResults = () => {
    if (loading) return <Loader color="gray" size={48} strokeWidth={4} />;

    if (!resultHits?.length)
      return (
        <ResetQueryMessage
          type="arret"
          translationId="page/components/SearchQuerySection.tsx/6"
          articleTitle={articleTitle}
          query={currentQuery}
          onResetQueryMessageClicked={onClearClicked}
        />
      );
    if (resultHits) {
      return (
        <>
          <RawSearchResultDecisions
            // @ts-ignore
            decisions={resultHits}
            updateDecisionsFolders={updateDecisionsFolders}
            updateFolders={updateFolders}
            loggedIn
            enableSnippets={false}
            deactivateScroll
            deactivateFeedbackBox
            modalParam="decisions"
            pathname={pathname}
            source={source}
          />
          {!resultTheEnd && (
            <SeeMoreMessage
              type="arret"
              query={currentQuery}
              articleIds={articleIds}
              loggedIn={loggedIn}
              filterValue={filterValue}
              resultTotalHits={resultTotalHits || 0}
              modal="decisions"
            />
          )}
          {resultTheEnd && currentQuery && (
            <ResetQueryMessage
              type="arret"
              translationId="page/components/SearchQuerySection.tsx/12"
              articleTitle={articleTitle}
              query={currentQuery}
              onResetQueryMessageClicked={onClearClicked}
            />
          )}
        </>
      );
    }
  };

  return (
    <>
      <SearchBarSection
        articleIds={articleIds}
        modal="decisions"
        type="arret"
        filterValue={filterValue}
        queryFromState={currentQuery}
        shouldPersistQuery={shouldPersistQuery}
        onClearClicked={onClearClicked}
      />
      <div className={styles.filtersToggleContainer}>
        {resultHits?.length ? (
          <FiltersToggle
            filters={memoizedFilters}
            filterParam={filterParam}
            onFilterClick={(value) => onFilterClick(value)}
            disableFilterClick={!loggedIn}
          />
        ) : null}
      </div>
      {renderFilterResults()}
    </>
  );
};

export const SearchQuerySectionDecisions = Connect(
  RawSearchQuerySectionDecisions,
);
